/* eslint-disable prettier/prettier */
/* eslint-disable arrow-body-style */
/**
 * 文件相关工具方法
 */

/**
 * 获取指定dataURL数据转换后的mimeType、streamData
 * @param dataURL dataURL
 * @returns 返回 dataURL 转换对应的 mimeType、streamData数据
 */
const getMimeTypeAndStreamDataFromDataURL = (
  dataURL: string,
): { mimeType: string; streamData: Uint8Array } => {
  const arr = dataURL.split(',');
  const mimeType = arr?.[0]?.match(/:(.*?);/)?.[1] || '';
  const bstr = atob(arr[1] || '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return {
    mimeType,
    streamData: u8arr,
  };
};

/**
 * 转换文件对象为dataURL
 * @param file 文件对象
 * @returns 文件对象的dataURL
 */
export const file2DataURL = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      resolve((fr?.result as string) || '');
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fr.onerror = (e: ProgressEvent<FileReader>) => {
      const errMsg =
        fr?.error?.message || e?.target?.error?.message || '未知异常';
      // @ts-ignore
      reject(new Error(`文件读取异常: ${errMsg} (${file?.name}文件)`));
      return 0 as any;
    };
    fr.readAsDataURL(file);
  });

/**
 * 转换dataURL为Blob对象
 * @param dataURL 数据dataURL
 * @returns 数据dataURL对应的Blob对象
 */
export const dataURL2Blob = (dataURL: string): Blob => {
  const { mimeType, streamData } = getMimeTypeAndStreamDataFromDataURL(dataURL);

  return new Blob([streamData], { type: mimeType });
};

/**
 * 转换dataURL为File对象
 * @param dataURL 数据dataURL
 * @param fileName 文件名称
 * @returns 数据dataURL对应的File对象
 */
export const dataURL2File = (dataURL: string, fileName: string): File => {
  const { mimeType, streamData } = getMimeTypeAndStreamDataFromDataURL(dataURL);

  return new File([streamData], fileName, { type: mimeType });
};

/**
 * blob转换为file对象
 * @param blob blob对象
 * @param fileName 文件名
 * @returns 返回blob对象对应的file对象
 */
export const blob2File = (blob: Blob, fileName: string): File =>
  new File([blob], fileName, { type: blob?.type });

/**
 * Image对象转换为File对象
 * @param image Image对象
 * @param fileName 文件名
 * @returns Image的文件
 */
export const image2File = (image: HTMLImageElement, fileName: string): File => {
  const cvs = document.createElement('canvas');
  const ctx = cvs.getContext('2d');
  const w = image?.width || 0;
  const h = image?.height || 0;
  let dataURL = '';
  cvs.width = w;
  cvs.height = h;
  ctx?.drawImage(image, 0, 0, w, h);
  dataURL = cvs.toDataURL('image/jpeg');
  return dataURL2File(dataURL, fileName);
};

/**
 * txt转换成文件
 * @param content 字符串
 * @param type 文件类型
 * @param fileName 文件名
 * @returns void
 */
export const txt2File = (content: string, type = 'text/markdown', fileName = 'filename.md') => {
  // 1. 将数据转换为文本内容
  const filtContent = new Blob([content], {type});
  // 2. 使用URL.createObjectURL()创建一个指向Blob对象的URL
  const url = URL.createObjectURL(filtContent);

  // 3. 创建一个anchor元素并设置相应的属性
  const anchorElem = document.createElement('a');
  anchorElem.href = url;
  anchorElem.download = fileName;
  anchorElem.style.display = 'none';
  // 4. 将anchor元素添加到DOM中，触发点击下载，然后移除
  document.body.appendChild(anchorElem);
  anchorElem.click();
  document.body.removeChild(anchorElem);

  // 5. 释放URL.createObjectURL()创建的URL
  URL.revokeObjectURL(url);
};

// 上传文件校验结果中的 帮助信息，用于业务拿到校验结果后，做自定义处理
interface ICheckUploadFilesHelperInfo {
  // 当前上传文件数量
  filesLength: number;
  // 最大允许上传文件数量
  maxFilesLength: number;
  // 允许上传的文件类型
  acceptMimeTypes: string[];
  // 不符合要求的文件类型，仅在文件的mimeType检验失败时，mimeTypes数组才会有值
  mimeTypes: string[];
  // 文件大小限制
  limitSize?: number;
  // 有文件大小限制时，超过限制的文件
  overLimitSizeFiles?: FileList | null;
}
// 上传文件校验结果
interface ICheckUploadFilesResult {
  // 本次上传的文件列表是否有效
  isValid: boolean;
  // 1 数量异常、2 文件类型异常、3 文件大小超过限制异常、999未知异常（files格式不对），校验成功时，不返回这个字段
  type?: 1 | 2 | 3 | 999;
  // 上传文件校验结果中的 帮助信息，用于业务拿到校验结果后，做自定义处理，校验成功时，不返回这个字段
  data?: ICheckUploadFilesHelperInfo;
}
// 获取文件MimeType的方法
export type IMimeTypeGetter = (file: File | null | undefined) => string;
// 开启批量上传时，并发上传文件数量限制
const MAX = 6;
// 创建文件校验结果对象
const buildCheckUploadFilesResult = (isValid: boolean, type?: 1 | 2 | 3 | 999, data?: ICheckUploadFilesHelperInfo): ICheckUploadFilesResult => {
  if (isValid) return { isValid };
  return {
    isValid,
    type,
    data: {
      filesLength: data?.filesLength || 0,
      maxFilesLength: data?.maxFilesLength || 0,
      acceptMimeTypes: data?.acceptMimeTypes || [],
      mimeTypes: data?.mimeTypes || [],
      limitSize: data?.limitSize,
      overLimitSizeFiles: data?.overLimitSizeFiles,
    },
  };
};
const getFileExtension = (fileName: string): string => {
  const name = fileName?.toLowerCase() || '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex < 0) {
    return '';
  }
  return name.slice(dotIndex);
};

const isFileAccepted = (
  file: File | null | undefined,
  accept: string[],
  getMimeType?: IMimeTypeGetter,
): boolean => {
  if (!file) {
    return false;
  }

  const mimeType = (getMimeType?.(file) || file?.type || '').toLowerCase();
  const fileExtension = getFileExtension(file?.name || '');
  return accept?.some((acceptItem) => {
    const normalizedAcceptItem = acceptItem?.trim()?.toLowerCase() || '';
    if (normalizedAcceptItem.startsWith('.')) {
      return normalizedAcceptItem === fileExtension;
    }
    if (normalizedAcceptItem.endsWith('/*')) {
      const acceptMainType = normalizedAcceptItem.slice(0, normalizedAcceptItem.indexOf('/'));
      const fileMainType = mimeType.slice(0, mimeType.indexOf('/'));
      return !!acceptMainType && acceptMainType === fileMainType;
    }
    return normalizedAcceptItem === mimeType;
  });
};

// 获取无效的mimeType
const getInvalidMimeTypes = (files: FileList, accept: string[], getMimeType?: IMimeTypeGetter): string[] => {
  if (!files?.length) return [];
  const map: { [key: string]: boolean } = {};
  const mimeTypes: string[] = [];
  for (const file of files) {
    const mimeType = getMimeType?.(file) || file?.type || getFileExtension(file?.name || '');
    if (!map[mimeType] && !isFileAccepted(file, accept, getMimeType)) {
      mimeTypes.push(mimeType);
      map[mimeType] = true;
    }
  }
  return mimeTypes;
};
// 获取超过大小限制的文件
const getOverLimitSizeFiles = (files: FileList, limitSize: number): File[] => {
  if (!files?.length) return [];
  return Array.prototype.filter.call(files, (file) => file?.size > limitSize);
};
// 批量上传文件校验
const checkUploadFilesMultiple = (
  files: FileList | null,
  options: {
    max: number;
    accept: string[];
    limitSize?: number;
    getMimeType?: IMimeTypeGetter;
  },
): ICheckUploadFilesResult => {
  const filesLength = files?.length || 0;
  const { max, accept, limitSize, getMimeType } = options || {};
  let maxNum = Number.parseInt(max as any, 10);
  maxNum = Number.isNaN(maxNum) ? MAX : maxNum;
  const baseHelperInfo = {
    maxFilesLength: maxNum,
    acceptMimeTypes: accept,
  };
  // 异常情况，几乎不存在
  if (!files) return buildCheckUploadFilesResult(false, 999, {
    ...baseHelperInfo,
    filesLength: 0,
    mimeTypes: [],
  });
  // 上传文件数量超过限制数量
  if (filesLength > maxNum) return buildCheckUploadFilesResult(false, 1, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: [],
  });
  // 上传文件中存在mimeType不符合要求的文件
  if (!Array.prototype.every.call(files, (file) => isFileAccepted(file, accept, getMimeType))) return buildCheckUploadFilesResult(false, 2, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: getInvalidMimeTypes(files, accept, getMimeType),
  });
  // 有文件大小限制，并且存在个别文件大小超过了限制，不符合要求
  if (typeof limitSize === 'number' && Array.prototype.some.call(files, (file) => file?.size > limitSize)) return buildCheckUploadFilesResult(false, 3, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: [],
    limitSize,
    overLimitSizeFiles: getOverLimitSizeFiles(files, limitSize) as any,
  });
  return buildCheckUploadFilesResult(true);
};
// 单文件上传文件校验
const checkUploadFilesSingle = (files: FileList | null, options: {
  accept: string[];
  limitSize?: number;
  getMimeType?: IMimeTypeGetter;
}): ICheckUploadFilesResult => {
  const { accept, limitSize, getMimeType } = options || {};
  const filesLength = files?.length || 0;
  const baseHelperInfo = {
    maxFilesLength: 1,
    acceptMimeTypes: accept,
  };
  // 异常情况，几乎不存在
  if (!filesLength) return buildCheckUploadFilesResult(false, 999, {
    ...baseHelperInfo,
    filesLength: 0,
    mimeTypes: [],
  });
  // 上传文件数量超过限制数量
  if (filesLength > 1) return buildCheckUploadFilesResult(false, 1, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: [],
  });
  const file = files?.[0];
  const fileMimeType = getMimeType?.(file) || file?.type || getFileExtension(file?.name || '');
  // 上传文件中存在mimeType不符合要求的文件
  if (!isFileAccepted(file, accept, getMimeType)) return buildCheckUploadFilesResult(false, 2, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: [fileMimeType],
  });
  // 有文件大小限制，并且文件大小超过了限制，不符合要求
  if (typeof limitSize === 'number' && (file?.size as number) > limitSize) return buildCheckUploadFilesResult(false, 3, {
    ...baseHelperInfo,
    filesLength,
    mimeTypes: [],
    limitSize,
    overLimitSizeFiles: files,
  });
  return buildCheckUploadFilesResult(true);
};
// 文件上传校验
export const checkUploadFiles = (
  files: FileList | null,
  options: {
    multiple: boolean;
    max: number;
    accept: string[];
    limitSize?: number;
    getMimeType?: IMimeTypeGetter;
  },
): ICheckUploadFilesResult => {
  const { multiple } = options || {};
  return multiple ? checkUploadFilesMultiple(files, options) : checkUploadFilesSingle(files, options);
};

/**
 * @description 下载文件
 * @date 2025-10-01
 * @author lv.chen
 * @param file 文件对象或文件URL
 * @param fileName 文件名
 */
export const downloadFile = (file: File | Blob | string, fileName: string): void => {
  let url: string;
  let shouldRevokeUrl = false;

  if (typeof file === 'string') {
    // 如果是URL字符串
    url = file;
  } else {
    // 如果是File或Blob对象
    url = URL.createObjectURL(file);
    shouldRevokeUrl = true;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 释放URL对象
  if (shouldRevokeUrl) {
    URL.revokeObjectURL(url);
  }
};
